<script setup lang="ts">
/**
 * SearchPanel - 專業搜尋面板 (Production Ready)
 * 🚨 [架構修復]：
 * 1. Zod 防呆：導入 .refine() 確保起迄點不得相同。
 * 2. Z-index 修復：確保下拉選單 (z-[100]) 不被相鄰元件遮擋。
 * 3. RWD 體驗：行動版 Pax 選單改用 Bottom Sheet 呈現。
 */
import { z } from "zod";
import { vOnClickOutside } from "@vueuse/components";

import { enumToOptions } from "@@/types/common";
import {
  TripType as tripTypes,
  CabinType as cabinTypes,
} from "@@/types/flights";
import ScholarBottomSheet from "~/components/shared/ScholarBottomSheet.vue";
import FlightsCalendarPricePicker from "./CalendarPricePicker.vue";

interface ITripFormItem {
  origin: string;
  destination: string;
  date: string;
  id: string;
}

const props = withDefaults(
  defineProps<{ initialQuery?: Record<string, any>; buttonLabel?: string }>(),
  { initialQuery: () => ({}), buttonLabel: "Search Flights" },
);

const router = useRouter();

// 🚨 導入 .refine() 進行嚴格商業邏輯防呆
const baseOrFormSchema = z.object({
  origin: z.string().min(1, { error: "Please enter departure city!" }),
  destination: z.string().min(1, { error: "Please enter destination!" }),
  departureDate: z.string().min(1, { error: "Please select departuredate!" }),
  returnDate: z.string().optional(),
});

const orFormSchema = baseOrFormSchema.refine(
  (data) => data.origin.toLowerCase() !== data.destination.toLowerCase(),
  {
    message: "Origin and destination cannot be the same.",
    path: ["destination"],
  },
);

const multyFormSchema = z
  .array(
    z.object({
      origin: z.string("Origin is required!"),
      destination: z.string("Destination is required!"),
      date: z.string("DepartureDate is required!"),
      id: z.string(),
    }),
  )
  .min(1);

const form = reactive({
  origin: "",
  destination: "",
  departureDate: "",
  returnDate: "",
  adults: 1,
  children: 0,
  infants: 0,
  cabin: cabinTypes.Economy,
  tripType: tripTypes.Oneway,
});
const multyTrips = ref<ITripFormItem[]>([
  {
    origin: "",
    destination: "",
    date: "",
    id: useId(),
  },
]);

const validationError = ref("");
const isCalendarOpen = ref(false);
const activeDateField = ref<"departureDate" | "returnDate">("departureDate");
const paxVisible = ref(false);
const isDesktopPaxOpen = ref(false);
const tripTypeOptions = enumToOptions(tripTypes);

const cabinOptions = enumToOptions(cabinTypes).filter((c) =>
  [cabinTypes.Economy, cabinTypes.Business, cabinTypes.PremiumEconomy].includes(
    c.value,
  ),
);
const summaryPaxCabin = computed(() => {
  const pax = form.adults + form.children + form.infants;
  const cabinText = cabinOptions.find((c) => c.value === form.cabin);
  return `${pax} ${pax > 1 ? "Paxs" : "Pax"}, ${cabinText?.label || ""}`;
});
const now = new Date();
const orMinDate = computed(() => {
  if (form.tripType === tripTypes.Oneway) {
    return now;
  } else if (form.tripType === tripTypes.RountTrip) {
    if (activeDateField.value === "departureDate") return now;

    return form.departureDate ? form.departureDate : now;
  }
});

const syncForm = () => {
  // form.date = String(
  //   props.initialQuery.date || props.initialQuery.dt || "",
  // ).trim();
  form.adults = Math.max(
    1,
    Number.parseInt(
      String(props.initialQuery.adults || props.initialQuery.adt || 1),
      10,
    ) || 1,
  );
  form.children = Math.max(
    0,
    Number.parseInt(
      String(props.initialQuery.children || props.initialQuery.chd || 0),
      10,
    ) || 0,
  );

  form.cabin = props.initialQuery.cabin || cabinTypes.Economy;
  form.tripType = props.initialQuery.tripType || tripTypes.Oneway;

  if (props.initialQuery?.trips) {
    if (form.tripType === tripTypes.MultiCities) {
      multyTrips.value = props.initialQuery?.trips.split("|").map((trip) => {
        const [origin, destination, date] = trip.split(",");

        return {
          origin,
          destination,
          date,
          id: useId(),
        };
      });
    } else {
      const [origin, destination, departureDate, returnDate] =
        props.initialQuery.trips.split(",");

      form.origin = origin;
      form.destination = destination;
      form.departureDate = departureDate;

      if (returnDate) {
        form.returnDate = returnDate;
      }
    }
  }
};

function handleRemove(index: number) {
  multyTrips.value.splice(index, 1);
}

function addNewTrip() {
  multyTrips.value.push({
    origin: "",
    destination: "",
    date: "",
    id: useId(),
  });
}

// 同步路由參數
watch(
  () => props.initialQuery,
  () => syncForm(),
  { deep: true, immediate: true },
);

const openCalendar = (field: "departureDate" | "returnDate") => {
  activeDateField.value = field;
  isCalendarOpen.value = true;
};

const swapLocations = () => {
  [form.origin, form.destination] = [form.destination, form.origin];
};

const isMultyCalendarOpen = ref(false);
const activeDateIndex = ref(0);
const openMultyCalendar = (index: number) => {
  activeDateIndex.value = index;
  isMultyCalendarOpen.value = true;
};
function handleUpdateDate(val: string) {
  console.log(val);

  if (multyTrips.value[activeDateIndex.value]) {
    multyTrips.value[activeDateIndex.value]!.date = val;
  }
}

const multiMinDate = computed(() => {
  if (multyTrips.value.length <= 1 || activeDateIndex.value === 0) {
    return now;
  } else {
    return multyTrips.value[activeDateIndex.value - 1]?.date;
  }
});

function validForm() {
  let result = null;
  if (form.tripType === tripTypes.Oneway) {
    const schema = baseOrFormSchema.omit({ returnDate: true });

    result = schema.safeParse(form);
  } else if (form.tripType === tripTypes.RountTrip) {
    result = orFormSchema.safeParse(form);
  } else if (form.tripType === tripTypes.MultiCities) {
    result = multyFormSchema.safeParse(multyTrips.value);
  }

  if (result) {
    if (result.success) return Promise.resolve(true);

    return Promise.reject("Please ensure form complete!");
  }

  return Promise.reject("Invalid form state!");
}

const submit = async () => {
  validationError.value = "";

  try {
    await validForm();
  } catch (e) {
    validationError.value = e.message || e;
    return;
  }

  const query: Record<string, string> = {
    tripType: form.tripType,
    adults: String(form.adults),
    ...(form.children > 0 ? { children: String(form.children) } : {}),
    ...(form.infants > 0 ? { infants: String(form.infants) } : {}),
    cabin: form.cabin,
    trips: "",
  };

  if (form.tripType === tripTypes.Oneway) {
    query.trips = [form.origin, form.destination, form.departureDate].join(",");
  } else if (form.tripType === tripTypes.RountTrip) {
    query.trips = [
      form.origin,
      form.destination,
      form.departureDate,
      form.returnDate,
    ].join(",");
  } else if (form.tripType === tripTypes.MultiCities) {
    query.trips = multyTrips.value
      .map((trip) => {
        return [trip.origin, trip.destination, trip.date].join(",");
      })
      .join("|");
  }

  await router.push({ path: "/flights/search", query });
};
// 處理桌面版外部點擊關閉下拉選單
const closeDesktopPax = () => {
  isDesktopPaxOpen.value = false;
};
</script>
<template>
  <div class="bg-white/50 rounded-[2rem] relative">
    <!-- 頂部控制列 (單程/來回 & 旅客選單) -->
    <div class="flex flex-col sm:flex-row gap-3 mb-5">
      <div class="flex bg-slate-100/80 p-1.5 rounded-2xl w-full sm:w-auto">
        <button
          v-for="type in tripTypeOptions"
          :key="type.value"
          class="flex-1 sm:px-6 py-2.5 rounded-xl text-xs sm:text-sm font-black transition-all"
          :class="
            form.tripType === type.value
              ? 'bg-white shadow-sm text-brand-600'
              : 'text-slate-500 hover:text-slate-700'
          "
          @click="form.tripType = type.value"
        >
          {{ type.label }}
        </button>
      </div>
      <!-- 手機版 Pax 觸發按鈕 -->
      <button
        class="md:hidden w-full sm:px-6 py-3 rounded-2xl text-sm font-black flex justify-center gap-3 items-center bg-slate-100/80 text-slate-700 active:bg-slate-200"
        @click="paxVisible = true"
      >
        <Icon name="ph:users-bold" size="18" />
        <span>{{ summaryPaxCabin }}</span>
      </button>
      <!-- 桌面版 Pax 下拉選單 (Z-index 100 防護) -->
      <div
        class="hidden md:block relative z-[100]"
        v-on-click-outside="closeDesktopPax"
      >
        <button
          class="h-full px-6 py-2 rounded-2xl text-sm font-black flex justify-center gap-3 items-center bg-slate-100/80 hover:bg-slate-200/80 transition-colors text-slate-700"
          @click="isDesktopPaxOpen = !isDesktopPaxOpen"
        >
          <Icon name="ph:users-bold" size="18" />
          <span>{{ summaryPaxCabin }}</span>
          <Icon
            :name="isDesktopPaxOpen ? 'ph:caret-up-bold' : 'ph:caret-down-bold'"
            class="text-xs text-slate-400"
          />
        </button>
        <Transition name="fade-slide">
          <div
            v-if="isDesktopPaxOpen"
            class="absolute left-0 top-full mt-2 w-[340px] bg-white rounded-2xl shadow-2xl border border-slate-100 p-5"
          >
            <!-- 艙等選擇 -->
            <div class="flex bg-slate-50 p-1 rounded-xl w-full mb-6">
              <button
                v-for="cabin in cabinOptions"
                :key="cabin.value"
                class="flex-1 px-2 py-2 rounded-lg text-xs font-black transition-all"
                :class="
                  form.cabin === cabin.value
                    ? 'bg-white shadow-sm text-brand-600'
                    : 'text-slate-400 hover:text-slate-600'
                "
                @click="form.cabin = cabin.value"
              >
                {{ cabin.label }}
              </button>
            </div>
            <!-- 人數調整 -->
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-black text-slate-900">Adults</p>
                  <p
                    class="text-[10px] text-slate-400 font-bold uppercase tracking-widest"
                  >
                    16+ years
                  </p>
                </div>
                <div
                  class="flex items-center gap-4 bg-slate-50 p-1.5 rounded-xl border border-slate-100"
                >
                  <button
                    class="w-8 h-8 flex items-center justify-center bg-white shadow-sm rounded-lg hover:text-brand-600 disabled:opacity-50"
                    :disabled="form.adults <= 1"
                    @click="form.adults--"
                  >
                    <Icon name="ph:minus-bold" size="16" />
                  </button>
                  <span class="w-4 text-center font-black">{{
                    form.adults
                  }}</span>
                  <button
                    class="w-8 h-8 flex items-center justify-center bg-white shadow-sm rounded-lg hover:text-brand-600"
                    @click="form.adults++"
                  >
                    <Icon name="ph:plus-bold" size="16" />
                  </button>
                </div>
              </div>
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-black text-slate-900">Children</p>
                  <p
                    class="text-[10px] text-slate-400 font-bold uppercase tracking-widest"
                  >
                    1-15 years
                  </p>
                </div>
                <div
                  class="flex items-center gap-4 bg-slate-50 p-1.5 rounded-xl border border-slate-100"
                >
                  <button
                    class="w-8 h-8 flex items-center justify-center bg-white shadow-sm rounded-lg hover:text-brand-600 disabled:opacity-50"
                    :disabled="form.children <= 0"
                    @click="form.children--"
                  >
                    <Icon name="ph:minus-bold" size="16" />
                  </button>
                  <span class="w-4 text-center font-black">{{
                    form.children
                  }}</span>
                  <button
                    class="w-8 h-8 flex items-center justify-center bg-white shadow-sm rounded-lg hover:text-brand-600"
                    @click="form.children++"
                  >
                    <Icon name="ph:plus-bold" size="16" />
                  </button>
                </div>
              </div>
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-black text-slate-900">Infants</p>
                  <p
                    class="text-[10px] text-slate-400 font-bold uppercase tracking-widest"
                  >
                    0-1 year
                  </p>
                </div>
                <div
                  class="flex items-center gap-4 bg-slate-50 p-1.5 rounded-xl border border-slate-100"
                >
                  <button
                    class="w-8 h-8 flex items-center justify-center bg-white shadow-sm rounded-lg hover:text-brand-600 disabled:opacity-50"
                    :disabled="form.infants <= 0"
                    @click="form.infants--"
                  >
                    <Icon name="ph:minus-bold" size="16" />
                  </button>
                  <span class="w-4 text-center font-black">{{
                    form.infants
                  }}</span>
                  <button
                    class="w-8 h-8 flex items-center justify-center bg-white shadow-sm rounded-lg hover:text-brand-600"
                    @click="form.infants++"
                  >
                    <Icon name="ph:plus-bold" size="16" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
    <!-- 主要輸入區 -->
    <div
      v-if="
        form.tripType === tripTypes.Oneway ||
        form.tripType === tripTypes.RountTrip
      "
      class="grid grid-cols-1 lg:grid-cols-12 gap-3 relative z-10"
    >
      <!-- 起迄點 -->
      <div
        class="lg:col-span-7 relative flex flex-col md:flex-row bg-slate-50/80 border border-slate-200 rounded-[1.5rem] shadow-sm"
      >
        <div
          class="flex-1 relative px-5 py-4 focus-within:bg-white focus-within:rounded-[1.5rem] transition-colors"
        >
          <label
            class="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5"
            >Flying From</label
          >
          <input
            v-model="form.origin"
            type="text"
            placeholder="City or Airport (e.g. LAX)"
            class="w-full bg-transparent text-xl md:text-2xl font-black text-slate-900 outline-none border-none placeholder:text-slate-300 uppercase focus:ring-0 p-0"
          />
        </div>
        <!-- 交換按鈕 -->
        <button
          @click="swapLocations"
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-11 h-11 bg-white border border-slate-100 shadow-md rounded-full flex items-center justify-center text-brand-600 hover:bg-brand-50 transition-all hover:scale-105 active:scale-95 rotate-90 md:rotate-0"
        >
          <Icon name="ph:arrows-left-right-bold" class="text-xl" />
        </button>
        <div
          class="flex-1 relative px-5 py-4 md:border-l border-t md:border-t-0 border-slate-200 focus-within:bg-white focus-within:rounded-[1.5rem] transition-colors"
        >
          <label
            class="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5"
            >Flying To</label
          >
          <input
            v-model="form.destination"
            type="text"
            placeholder="City or Airport (e.g. JFK)"
            class="w-full bg-transparent text-xl md:text-2xl font-black text-slate-900 outline-none border-none placeholder:text-slate-300 uppercase focus:ring-0 p-0"
          />
        </div>
      </div>
      <!-- 日期選擇 -->
      <div
        class="lg:col-span-5 flex bg-slate-50/80 border border-slate-200 rounded-[1.5rem] shadow-sm"
      >
        <button
          class="flex-1 text-left px-5 py-4 hover:bg-white hover:rounded-[1.5rem] transition-colors"
          @click="openCalendar('departureDate')"
        >
          <span
            class="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5"
            >Departure</span
          >
          <span
            class="block text-xl md:text-2xl font-black"
            :class="form.departureDate ? 'text-slate-900' : 'text-slate-300'"
          >
            {{ form.departureDate || "Select" }}
          </span>
        </button>
        <div
          v-if="form.tripType === tripTypes.RountTrip"
          class="w-px bg-slate-200 my-4"
        ></div>
        <button
          v-if="form.tripType === tripTypes.RountTrip"
          class="flex-1 text-left px-5 py-4 hover:bg-white hover:rounded-[1.5rem] transition-colors"
          @click="openCalendar('returnDate')"
        >
          <span
            class="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5"
            >Return</span
          >
          <span
            class="block text-xl md:text-2xl font-black"
            :class="form.returnDate ? 'text-slate-900' : 'text-slate-300'"
          >
            {{ form.returnDate || "Select" }}
          </span>
        </button>
      </div>
    </div>

    <div v-else-if="form.tripType === tripTypes.MultiCities" class="space-y-5">
      <div
        v-for="(trip, index) in multyTrips"
        :key="trip.id"
        class="grid grid-cols-1 lg:grid-cols-12 gap-3 relative z-10"
      >
        <div class="col-span-12 font-black uppercase tracking-widest">
          Trip {{ index + 1 }}
        </div>
        <!-- 起迄點 -->
        <div
          class="col-span-12 lg:col-span-7 relative flex flex-col md:flex-row bg-slate-50/80 border border-slate-200 rounded-[1.5rem] shadow-sm"
        >
          <div
            class="flex-1 relative px-5 py-4 focus-within:bg-white focus-within:rounded-[1.5rem] transition-colors"
          >
            <label
              class="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5"
              >Flying From</label
            >
            <input
              v-model="trip.origin"
              type="text"
              placeholder="City or Airport (e.g. LAX)"
              class="w-full bg-transparent text-xl md:text-2xl font-black text-slate-900 outline-none border-none placeholder:text-slate-300 uppercase focus:ring-0 p-0"
            />
          </div>

          <div
            class="flex-1 relative px-5 py-4 md:border-l border-t md:border-t-0 border-slate-200 focus-within:bg-white focus-within:rounded-[1.5rem] transition-colors"
          >
            <label
              class="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5"
              >Flying To</label
            >
            <input
              v-model="trip.destination"
              type="text"
              placeholder="City or Airport (e.g. JFK)"
              class="w-full bg-transparent text-xl md:text-2xl font-black text-slate-900 outline-none border-none placeholder:text-slate-300 uppercase focus:ring-0 p-0"
            />
          </div>
        </div>
        <!-- 日期選擇 -->
        <div class="col-span-12 lg:col-span-5 flex gap-3">
          <div
            class="flex-1 flex bg-slate-50/80 border border-slate-200 rounded-[1.5rem] shadow-sm"
          >
            <button
              class="flex-1 text-left px-5 py-4 hover:bg-white hover:rounded-[1.5rem] transition-colors"
              @click="openMultyCalendar(index)"
            >
              <span
                class="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5"
                >Departure</span
              >
              <span
                class="block text-xl md:text-2xl font-black"
                :class="trip.date ? 'text-slate-900' : 'text-slate-300'"
              >
                {{ trip.date || "Select" }}
              </span>
            </button>
          </div>

          <div
            v-if="index > 0"
            class="w-full sm:w-28 bg-slate-50 rounded-[1.5rem] p-2 flex items-center justify-center"
          >
            <button
              class="flex items-center justify-center p-2 text-red-500"
              @click="handleRemove(index)"
            >
              <Icon name="lucide-trash-2" size="32" />
            </button>
          </div>
        </div>
      </div>

      <div v-if="form.tripType === tripTypes.MultiCities" class="py-5">
        <button
          @click="addNewTrip"
          class="flex items-center gap-2 text-xs font-black text-brand-600 uppercase tracking-widest hover:underline"
        >
          <Icon name="ph:plus-circle-bold" /> Add another flight
        </button>
      </div>
    </div>

    <!-- 驗證錯誤 & 提交 -->
    <div
      class="mt-6 flex flex-col md:flex-row items-center justify-between gap-4"
    >
      <div class="flex-1">
        <p
          v-if="validationError"
          class="text-xs font-bold text-red-500 flex items-center gap-1.5 bg-red-50 px-3 py-1.5 rounded-lg inline-flex"
        >
          <Icon name="ph:warning-circle-fill" /> {{ validationError }}
        </p>
      </div>
      <button
        @click="submit"
        class="w-full md:w-auto btn-action !py-4 !px-12 text-lg lg:text-xl font-black uppercase tracking-tighter shadow-xl shadow-action-500/20 active:scale-95 transition-transform flex justify-center"
      >
        <Icon name="ph:magnifying-glass-bold" class="text-2xl" />
        {{ buttonLabel }}
      </button>
    </div>
    <!-- 彈窗組件 -->
    <FlightsCalendarPricePicker
      v-model="form[activeDateField]"
      :is-open="isCalendarOpen"
      :origin="form.origin"
      :destination="form.destination"
      :min-date="orMinDate"
      @close="isCalendarOpen = false"
    />
    <FlightsCalendarPricePicker
      :model-value="multyTrips[activeDateIndex]?.date"
      :is-open="isMultyCalendarOpen"
      :min-date="multiMinDate"
      @update:model-value="handleUpdateDate"
      @close="isMultyCalendarOpen = false"
    />

    <ScholarBottomSheet v-model="paxVisible" title="Passengers & Class">
      <!-- (復用 Desktop Pax 的內容，為 Mobile 提供舒適大按鈕) -->
      <div class="flex bg-slate-100 p-1.5 rounded-xl w-full mb-8">
        <button
          v-for="cabin in cabinOptions"
          :key="cabin.value"
          class="flex-1 px-2 py-3 rounded-lg text-sm font-black"
          :class="
            form.cabin === cabin.value
              ? 'bg-white shadow text-brand-600'
              : 'text-slate-500'
          "
          @click="form.cabin = cabin.value"
        >
          {{ cabin.label }}
        </button>
      </div>
      <div class="space-y-6 px-2">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-lg font-black text-slate-900">Adults</p>
            <p
              class="text-xs text-slate-400 font-bold uppercase tracking-widest"
            >
              16+ years
            </p>
          </div>
          <div
            class="flex items-center gap-6 bg-slate-50 p-2 rounded-2xl border border-slate-100"
          >
            <button
              class="w-12 h-12 flex items-center justify-center bg-white shadow-sm rounded-xl"
              :disabled="form.adults <= 1"
              @click="form.adults--"
            >
              <Icon name="ph:minus-bold" size="20" /></button
            ><span class="w-4 text-center font-black text-xl">{{
              form.adults
            }}</span
            ><button
              class="w-12 h-12 flex items-center justify-center bg-white shadow-sm rounded-xl"
              @click="form.adults++"
            >
              <Icon name="ph:plus-bold" size="20" />
            </button>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div>
            <p class="text-lg font-black text-slate-900">Children</p>
            <p
              class="text-xs text-slate-400 font-bold uppercase tracking-widest"
            >
              1-15 years
            </p>
          </div>
          <div
            class="flex items-center gap-6 bg-slate-50 p-2 rounded-2xl border border-slate-100"
          >
            <button
              class="w-12 h-12 flex items-center justify-center bg-white shadow-sm rounded-xl"
              :disabled="form.children <= 0"
              @click="form.children--"
            >
              <Icon name="ph:minus-bold" size="20" /></button
            ><span class="w-4 text-center font-black text-xl">{{
              form.children
            }}</span
            ><button
              class="w-12 h-12 flex items-center justify-center bg-white shadow-sm rounded-xl"
              @click="form.children++"
            >
              <Icon name="ph:plus-bold" size="20" />
            </button>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div>
            <p class="text-lg font-black text-slate-900">Infants</p>
            <p
              class="text-xs text-slate-400 font-bold uppercase tracking-widest"
            >
              0-1 year
            </p>
          </div>
          <div
            class="flex items-center gap-6 bg-slate-50 p-2 rounded-2xl border border-slate-100"
          >
            <button
              class="w-12 h-12 flex items-center justify-center bg-white shadow-sm rounded-xl"
              :disabled="form.infants <= 0"
              @click="form.infants--"
            >
              <Icon name="ph:minus-bold" size="20" /></button
            ><span class="w-4 text-center font-black text-xl">{{
              form.infants
            }}</span
            ><button
              class="w-12 h-12 flex items-center justify-center bg-white shadow-sm rounded-xl"
              @click="form.infants++"
            >
              <Icon name="ph:plus-bold" size="20" />
            </button>
          </div>
        </div>
      </div>
    </ScholarBottomSheet>
  </div>
</template>
<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
